import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../lib/api';
import type { BranchDto, BranchListDto } from '@repo/shared-types';
import type { CreateBranchInput, UpdateBranchInput } from '@repo/validation';
import { toast } from 'sonner';
import i18n from '../i18n';

// includeArchived is part of the KEY, never omitted from it: the settings page
// and the branch switcher read different row sets from the same endpoint, and
// sharing one cache entry would put archived branches in the switcher.
export function useBranches(opts?: { includeArchived?: boolean }) {
  const includeArchived = opts?.includeArchived ?? false;
  return useQuery<BranchListDto>({
    queryKey: ['branches', { includeArchived: includeArchived || null }],
    queryFn: () => fetchApi(includeArchived ? '/branches?includeArchived=1' : '/branches'),
  });
}

const inval = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['branches'] });
  // A branch rename shows on the slot cards and the switcher; an archive removes
  // it from both. Slots carry branchId, so their cache is stale either way —
  // and an adopt-on-create moves slots wholesale, which also changes what every
  // branch-scoped sessions/students list returns.
  qc.invalidateQueries({ queryKey: ['slots'] });
  // Sessions and students are filtered THROUGH the slot, so a bulk move
  // changes what every branch-scoped list returns, not just the slots. Every
  // `inval()` caller can move slots in bulk (adopt-on-create here,
  // assign-unassigned below), so this lives here rather than being repeated
  // per caller.
  qc.invalidateQueries({ queryKey: ['sessions'] });
  qc.invalidateQueries({ queryKey: ['students'] });
};

export function useCreateBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBranchInput) => fetchApi<BranchDto>('/branches', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { inval(qc); toast.success(i18n.t('branches.created', { ns: 'settings' })); },
    onError: (e: Error) => toast.error(e.message || i18n.t('branches.error', { ns: 'settings' })),
  });
}

export function useRenameBranch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateBranchInput) => fetchApi<BranchDto>(`/branches/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { inval(qc); toast.success(i18n.t('branches.renamed', { ns: 'settings' })); },
    onError: (e: Error) => toast.error(e.message || i18n.t('branches.error', { ns: 'settings' })),
  });
}

export function useArchiveBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetchApi<BranchDto>(`/branches/${id}/archive`, { method: 'POST' }),
    onSuccess: () => { inval(qc); toast.success(i18n.t('branches.archivedToast', { ns: 'settings' })); },
    onError: (e: Error) => toast.error(e.message || i18n.t('branches.error', { ns: 'settings' })),
  });
}

export function useUnarchiveBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetchApi<BranchDto>(`/branches/${id}/unarchive`, { method: 'POST' }),
    onSuccess: () => { inval(qc); toast.success(i18n.t('branches.unarchivedToast', { ns: 'settings' })); },
    onError: (e: Error) => toast.error(e.message || i18n.t('branches.error', { ns: 'settings' })),
  });
}

export function useAssignUnassigned() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (branchId: string) =>
      fetchApi<{ assigned: number }>(`/branches/${branchId}/assign-unassigned`, { method: 'POST' }),
    onSuccess: (data) => {
      inval(qc);
      toast.success(i18n.t('branches.bulkAssignDone', { ns: 'settings', count: data.assigned }));
    },
    onError: (e: Error) => toast.error(e.message || i18n.t('branches.error', { ns: 'settings' })),
  });
}
